import { useEffect, useMemo, useRef } from "react";

import { UIView, useCurrentStateAndParams } from "@uirouter/react";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";

import { customRangeWithDayFormat } from "@similarweb/pro-logic-components";
import { Box, Flex } from "@similarweb/ui-components/foundations";

import { showErrorToast } from "app/actions/toast_actions";
import { translate } from "app/filters/i18nFilter";
import { ReportLoadingBanner } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/components/banners/ReportLoadingBanner";
import { useLLMTabs } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/hooks/useLLMTabs";
import { useReportStatus } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/hooks/useReportStatus";
import {
    getDefaultModel,
    isModelOpenForUser,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";
import type { ILLMReport, LLModel } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { ReportStatus } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import type { DatesRange } from "app/pages/sales-intelligence/sub-modules/account-overview/shared/types/common";
import DurationService from "app/services/DurationService";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { useLLMTrackerCampaignById, useLLMTrackerCampaigns } from "./llm-tracker-service/useLLMTrackerCampaigns";
import { LLMReportContainerTabs } from "./LLMReportContainerTabs";
import { LLMTrackerSubNav } from "./LLMTrackerSubNav";

export const ReportContainer = () => {
    const {
        params: { campaignId, model, duration, isDurationChanged },
    } = useCurrentStateAndParams();

    const {
        currentTab: {
            id: currentTabId,
            data: { firstSupportedDate },
        },
    } = useLLMTabs();

    const dispatch = useDispatch();
    const { data: campaigns } = useLLMTrackerCampaigns();
    const { data: selectedCampaign } = useLLMTrackerCampaignById(campaignId);
    const contentContainerRef = useRef<HTMLDivElement>(null);
    const resolvedModel =
        model && isModelOpenForUser(model as LLModel, selectedCampaign.isDemo) && model in selectedCampaign.configs
            ? (model as LLModel)
            : getDefaultModel(selectedCampaign, currentTabId);

    const campaignConfig = selectedCampaign.configs?.[resolvedModel];
    const reportStatus = useReportStatus(selectedCampaign, campaignConfig);
    const { supportedDuration, isDurationModified } = useMemo<{
        supportedDuration: DatesRange;
        isDurationModified: boolean;
    }>(() => {
        const defaultDuration = DurationService.getDurationObjectForCampaigns(
            duration as string,
            campaignConfig?.lastScrapeDate,
        );

        const shouldModify =
            isDurationChanged === "false" &&
            firstSupportedDate &&
            defaultDuration.from.isBefore(firstSupportedDate, "day");

        return {
            supportedDuration: shouldModify
                ? {
                      from: firstSupportedDate.startOf("day"),
                      to: dayjs.max(defaultDuration.to, firstSupportedDate).endOf("day"),
                  }
                : defaultDuration,
            isDurationModified: shouldModify,
        };
    }, [duration, campaignConfig, firstSupportedDate, isDurationChanged]);

    const reportProps: ILLMReport = {
        campaign: selectedCampaign,
        campaignConfig: campaignConfig,
        reportDuration: supportedDuration,
        reportStatus: reportStatus,
    };

    useEffect(() => {
        if (selectedCampaign && reportStatus === ReportStatus.Error) {
            const errorMessage = translate("llm.campaign.status.failed");
            dispatch(showErrorToast(errorMessage));
        }
    }, [reportStatus, selectedCampaign?.id, dispatch]);

    //modify the duration, so it's compatible with the report's available data
    useEffect(() => {
        if (isDurationModified) {
            const { from, to } = supportedDuration;
            const newDuration = `${from.format(customRangeWithDayFormat)}-${to.format(customRangeWithDayFormat)}`;

            void proNavigator.updateParams({
                duration: newDuration,
                isDurationChanged: true,
            });
        }
    }, [isDurationModified, supportedDuration]);

    return (
        <Box padding={"0 0 0 188px"}>
            <div className="sw-layout-module sw-layout-no-scroll-container fadeIn sidebar3">
                <LLMTrackerSubNav
                    selectedCampaign={selectedCampaign}
                    allCampaigns={campaigns}
                    reportStatus={reportStatus}
                />
                <div
                    ref={contentContainerRef}
                    className="sw-layout-scrollable-element use-sticky-css-rendering"
                >
                    <Flex
                        flexDirection="column"
                        width="100%"
                        boxSizing="border-box"
                    >
                        <LLMReportContainerTabs
                            selectedCampaign={selectedCampaign}
                            containerRef={contentContainerRef}
                        />
                        <Flex
                            flexDirection="column"
                            margin="48px 0 0"
                            padding="0 44px 44px 44px"
                            gap="24px"
                            zIndex={10}
                        >
                            {reportStatus === ReportStatus.NotReady ? <ReportLoadingBanner /> : null}
                            <UIView
                                render={(Component, routedProps) => (
                                    <Component
                                        {...routedProps}
                                        {...reportProps}
                                    />
                                )}
                            />
                        </Flex>
                    </Flex>
                </div>
            </div>
        </Box>
    );
};
